import { createSignal, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { Portal } from "solid-js/web";
import { TransitionGroup } from "@fleet/ui";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "@fleet/icons";

import { TOAST_SWIPE } from "@/lib/gestures/presets";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";

type ToastType = "success" | "error" | "info" | "warning";

/** Inline toast action (e.g. "Undo"). */
interface ToastAction {
  label: string;
  onAction: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

const TOAST_DURATION = 5000;
const RADIX_36 = 36;
const ID_LENGTH = 9;
const [toasts, setToasts] = createStore<Toast[]>([]);

// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  if (typeof globalThis.matchMedia !== "function") {
    return false;
  }
  return globalThis.matchMedia(REDUCED_MOTION_QUERY).matches;
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  commitThresholdRatio: COMMIT_THRESHOLD_RATIO,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_VIBRATION_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
  fling: FLING,
} = TOAST_SWIPE;

const DISMISS_OVERSHOOT_PX = 32;
const OPACITY_FADE_DISTANCE_PX = 240;

export function showToast(message: string, type: ToastType = "info") {
  const id = Math.random().toString(RADIX_36).slice(2, ID_LENGTH);
  setToasts([...toasts, { id, message, type }]);

  setTimeout(() => {
    setToasts(toasts.filter((t) => t.id !== id));
  }, TOAST_DURATION);
}

const FLASH_ID = "__saved_flash__";
const FLASH_DURATION = 1400;
let flashTimer: ReturnType<typeof setTimeout> | undefined;

const UNDO_FLASH_ID = "__undo_flash__";
const UNDO_FLASH_DURATION = 6000;
let undoFlashTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Coalesced undoable toast (UNDO-TRASH-LIGHT L1): a single pill
 * "action done · Undo" that refreshes on each mutation instead of stacking
 * notifications (recording 5 services in a row = 1 living pill, not 5 toasts).
 * Auto-dismiss at ~6s; full history remains in undo stack
 * (Ctrl+Z steps back further than visible pill).
 */
export function showUndoFlash(message: string, action: ToastAction) {
  if (toasts.some((t) => t.id === UNDO_FLASH_ID)) {
    // Already visible: update in place (without re-animating) and refresh timer.
    setToasts((t) => t.id === UNDO_FLASH_ID, { message, action });
  } else {
    setToasts([...toasts, { id: UNDO_FLASH_ID, message, type: "success", action }]);
  }
  if (undoFlashTimer) {
    clearTimeout(undoFlashTimer);
  }
  undoFlashTimer = setTimeout(() => {
    dismissUndoFlash();
  }, UNDO_FLASH_DURATION);
}

/** Dismisses undo pill (after undoing, or when switching accounts). */
export function dismissUndoFlash() {
  if (undoFlashTimer) {
    clearTimeout(undoFlashTimer);
    undoFlashTimer = undefined;
  }
  setToasts(toasts.filter((t) => t.id !== UNDO_FLASH_ID));
}

/**
 * Coalesced "done" confirmation: a single short pill refreshing its timer on
 * each call (calm feedback without spam). Used by undo glue to
 * confirmar "Undone".
 */
export function showSavedFlash(message: string) {
  if (toasts.some((t) => t.id === FLASH_ID)) {
    setToasts((t) => t.id === FLASH_ID, "message", message);
  } else {
    setToasts([...toasts, { id: FLASH_ID, message, type: "success" }]);
  }
  if (flashTimer) {
    clearTimeout(flashTimer);
  }
  flashTimer = setTimeout(() => {
    setToasts(toasts.filter((t) => t.id !== FLASH_ID));
  }, FLASH_DURATION);
}

function removeToast(id: string) {
  setToasts(toasts.filter((t) => t.id !== id));
}

/**
 * A single toast card with swipe-to-dismiss. Built on the shared gesture
 * recognizer (deadzone, axis-lock, fling, rising-edge haptic). Single-axis
 * (`x`) so it never fights vertical page scroll; the card follows the finger in
 * either direction and dismisses past a fraction of its width or on a fling,
 * otherwise snaps back. Honours `prefers-reduced-motion` (instant, no slide).
 */
function ToastItem(props: { toast: Toast }) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let surfaceRef: HTMLDivElement | undefined;

  const dismiss = (direction: 1 | -1) => {
    if (prefersReducedMotion()) {
      removeToast(props.toast.id);
      return;
    }
    const width = surfaceRef?.clientWidth ?? FALLBACK_WIDTH_PX;
    setIsAnimating(true);
    setTx(direction * (width + DISMISS_OVERSHOOT_PX));
    setTimeout(() => {
      removeToast(props.toast.id);
    }, COMMIT_ANIMATION_MS);
  };

  const snapBack = () => {
    setIsAnimating(true);
    setTx(0);
    setTimeout(() => {
      setIsAnimating(false);
    }, CANCEL_ANIMATION_MS);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    fling: FLING,
    haptics: true,
    hapticMs: HAPTIC_VIBRATION_MS,
    // Ignore swipes that begin on an inline button (close / undo action) so
    // their taps still work.
    canStart: (info) =>
      !(
        info.target instanceof Element &&
        info.target.closest("[data-toast-close],[data-testid='toast-action']")
      ),
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      setTx(applyRubberBand(sample.dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: (sample) => {
      setIsSwiping(false);
      const width = surfaceRef?.clientWidth ?? FALLBACK_WIDTH_PX;
      const commitPx = Math.min(COMMIT_THRESHOLD_PX, width * COMMIT_THRESHOLD_RATIO);
      const finalTx = tx();
      const committed = Math.abs(finalTx) > commitPx || sample.isFling;
      if (committed) {
        dismiss(finalTx >= 0 ? 1 : -1);
      } else {
        snapBack();
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      snapBack();
    },
  });

  const handlers = pointerHandlers(recognizer);

  return (
    <div
      ref={surfaceRef}
      // CC-30: error toasts are announced ASSERTIVELY (`role="alert"`
      // + `aria-live="assertive"`) so a screen reader interrupts immediately
      // — a failure (4xx/5xx that `handleApiResponse` converts to visible
      // error) must never stay silent for screen reader users either.
      // The rest (success/info/warning) remains "polite".
      role={props.toast.type === "error" ? "alert" : "status"}
      aria-live={props.toast.type === "error" ? "assertive" : "polite"}
      class="flex items-center justify-between gap-3 p-4 rounded-lg border shadow-lg bg-popover select-none"
      classList={{
        "border-success/30 text-success": props.toast.type === "success",
        "border-destructive/30 text-destructive": props.toast.type === "error",
        "border-primary/30 text-primary": props.toast.type === "info",
        "border-warning/30 text-warning": props.toast.type === "warning",
        "transition-[transform,opacity] duration-200 ease-[var(--ease-elegant)]": isAnimating(),
      }}
      style={{
        // Inline styles ONLY during swipe gesture/animation: if applied
        // ALWAYS, inline `transform`/`opacity` OVERRIDE the <TransitionGroup>
        // .toast-exit-* of <TransitionGroup> and exit `transitionend` does
        // not fire → toasts never auto-dismissed after a partial swipe
        // (bug CC-20). At rest (no swipe, tx()===0) we let the CSS exit
        // transition do its work.
        ...(isSwiping() || isAnimating() || tx() !== 0
          ? {
              transform: `translate3d(${tx()}px, 0, 0)`,
              opacity:
                isSwiping() || isAnimating()
                  ? `${Math.max(0, 1 - Math.abs(tx()) / OPACITY_FADE_DISTANCE_PX)}`
                  : "1",
            }
          : {}),
        "touch-action": "pan-y",
        cursor: isSwiping() ? "grabbing" : "grab",
      }}
      onPointerDown={handlers.onPointerDown}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerCancel}
    >
      <div class="flex min-w-0 items-center gap-3">
        <Show when={props.toast.type === "success"}>
          <CheckCircle2 class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "error"}>
          <AlertCircle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "warning"}>
          <AlertTriangle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "info"}>
          <Info class="h-5 w-5 shrink-0" />
        </Show>
        <span class="min-w-0 break-words text-sm font-medium">{props.toast.message}</span>
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <Show when={props.toast.action}>
          {(action) => (
            <button
              type="button"
              data-testid="toast-action"
              onClick={() => {
                removeToast(props.toast.id);
                action().onAction();
              }}
              class="rounded-md px-2 py-1 text-sm font-semibold underline-offset-2 hover:underline hover:bg-black/5 transition-colors"
            >
              {action().label}
            </button>
          )}
        </Show>
        <button
          type="button"
          data-toast-close
          onClick={() => {
            removeToast(props.toast.id);
          }}
          class="shrink-0 p-1 hover:bg-black/5 rounded-full transition-colors"
        >
          <X class="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function Toaster() {
  return (
    <Portal>
      <div
        class="fixed z-[300] flex flex-col gap-2 pointer-events-none"
        style={{
          bottom: "calc(1rem + var(--safe-area-inset-bottom))",
          left: "calc(1rem + var(--safe-area-inset-left))",
          right: "calc(1rem + var(--safe-area-inset-right))",
        }}
      >
        <div class="flex flex-col gap-2 sm:ml-auto sm:w-[400px] sm:max-w-full [&>*]:pointer-events-auto">
          <TransitionGroup name="toast">
            <For each={toasts}>{(toast) => <ToastItem toast={toast} />}</For>
          </TransitionGroup>
        </div>
      </div>
    </Portal>
  );
}
